/* eslint-disable no-shadow */
import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { useDispatch } from 'react-redux';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

import { CHOICE_RESULT_KEYS } from '@Services/Editor/Constants';

import { updateChoiceResults } from '@Services/Editor';

import { ReactComponent as ResultDividerSVG } from '@Assets/Svgs/divider_0.5_vert.svg';
import HoverComponent from '@Components/Common/HoverComponent';
import NextProcessIdChip from './NextProcessIdChip';
import ProbabilityConditionChip from './Property/ProbabilityConditionChip';
import RequiredItemsChip from './Property/RequiredItemsChip';

import AddingPropertyButton from './Property/AddingPropertyButton';
import AddStoreItemChip from './Property/AddStoreItemChip';
import DestroyStoreItemChip from './Property/DestroyStoreItemChip';
import ChangeStatusChip from './Property/ChangeStatusChip';
import AchievementButton from './Property/AchievementButton';
import ResultShowMoreButton from './ResultShowMoreButton';

const Result = ({
  processId,
  choiceId,
  results,
  choiceIndex,
  snackBarRef,
  isChoiceHovered,
  isProcessTextEmpty,
  validatedProcess,
}) => {
  const dispatch = useDispatch();

  const isDisabledDeleteItem = results.length <= 1;

  const handleUpdateChoiceResults = useCallback(
    (afterItemIndex, beforeItemIndex) => {
      dispatch(updateChoiceResults({ processId, choiceId, afterItemIndex, beforeItemIndex }));
    },
    [dispatch],
  );

  const handleOnDragEnd = useCallback(
    result => {
      const isDefaultResult = results.findIndex(result => result.resultId === 0);

      if (result.destination.index === isDefaultResult) {
        snackBarRef.current.show({
          text: '기본 도착지 순서는 변경할 수 없습니다',
        });
        return;
      }

      if (!result.destination) {
        return;
      }
      const beforeDragItemIndex = result.source.index;
      const afterDragItemIndex = result.destination.index;

      handleUpdateChoiceResults(afterDragItemIndex, beforeDragItemIndex);
    },
    [results],
  );

  function getWarningMessage(
    isProcessTextEmpty,
    haveNotProbability,
    filledProbability,
    probability,
    haveNotIfs,
    nextProcessIdInvalid,
  ) {
    if (isProcessTextEmpty) return '';
    if (haveNotProbability) {
      return '확률이 비어있으면 실행되지 않아요. 1~99% 사이의 값을 입력해주세요.';
    }
    if (filledProbability && probability === 100) {
      return '무조건 실행되는 분기에요. 99% 이하의 확률을 입력해주세요.';
    }
    if (haveNotIfs) {
      return '보유 항목이 비어있으면 실행되지 않아요. 필요한 아이템을 입력해주세요.';
    }
    if (nextProcessIdInvalid) {
      return '도착 페이지가 없거나 완성되지 않았어요.';
    }

    return '';
  }

  return (
    <>
      <DragDropContext onDragEnd={handleOnDragEnd}>
        <Droppable droppableId={`${processId}:${choiceId}:result`}>
          {provided => (
            <DroppableContainer ref={provided.innerRef} {...provided.droppableProps}>
              {results.map(
                (
                  {
                    resultId,
                    nextProcessId,
                    resultPropertyChipState,
                    store,
                    storeHidden,
                    destroy,
                    destroyHidden,
                    requiredItems,
                    newAchievementId,
                    probability,
                  },
                  index,
                ) => {
                  const isExistProb = Object.keys(resultPropertyChipState).includes(CHOICE_RESULT_KEYS.PROBABILITY);

                  const isExistIfs = Object.keys(resultPropertyChipState).includes(CHOICE_RESULT_KEYS.REQUIRED_ITEMS);

                  const isShowMultipleLine = resultPropertyChipState[CHOICE_RESULT_KEYS.PROBABILITY];
                  const isAchievementChipVisible = !!resultPropertyChipState[CHOICE_RESULT_KEYS.ACHIEVEMENT];

                  const isDefaultResult = results.findIndex(result => result.resultId === 0);

                  const isDragDisabled = index === isDefaultResult; // 첫 번째 아이템에 대해 드래그 비활성화

                  const haveNotProbability = !isDragDisabled && !probability && !isExistIfs;

                  const filledProbability = !isDragDisabled && probability && !isExistIfs;

                  const haveNotIfs = !isDragDisabled && !resultPropertyChipState.requiredItems && !isExistProb;
                  const invalidNextProcess = !validatedProcess.includes(nextProcessId);

                  const warningMessage = getWarningMessage(
                    isProcessTextEmpty,
                    haveNotProbability,
                    filledProbability,
                    probability,
                    haveNotIfs,
                    invalidNextProcess,
                  );
                  const showWarning = warningMessage !== '';

                  return (
                    <Draggable
                      key={resultId}
                      index={index}
                      draggableId={`${processId}:${choiceId}:${resultId}`}
                      isDragDisabled={isDragDisabled}
                    >
                      {provided => (
                        <ResultContainer
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          isDefaultResult={!isDragDisabled}
                          isResultOnlyOne={isDisabledDeleteItem}
                        >
                          {showWarning && (
                            <HoverComponent
                              label={warningMessage}
                              style={{ left: -36, top: 12 }}
                              iconColor={({ theme }) => (isDragDisabled ? theme.colors.danger : theme.colors.primary)}
                              align="left"
                              absolute
                            />
                          )}

                          <LeftContainer>
                            <LeftTopContainer isShowMultipleLine={isShowMultipleLine}>
                              <NextProcessIdChip
                                processId={processId}
                                choiceId={choiceId}
                                resultId={resultId}
                                nextProcessId={nextProcessId}
                              />
                              <ResultDivider />
                              <ProbabilityConditionChip
                                processId={processId}
                                choiceId={choiceId}
                                resultId={resultId}
                                results={results}
                              />

                              <RequiredItemsChip
                                processId={processId}
                                choiceId={choiceId}
                                resultId={resultId}
                                requiredItems={requiredItems}
                              />
                              <AddStoreItemChip
                                processId={processId}
                                choiceId={choiceId}
                                resultId={resultId}
                                store={store}
                                storeHidden={storeHidden}
                              />
                              <DestroyStoreItemChip
                                processId={processId}
                                choiceId={choiceId}
                                resultId={resultId}
                                destroy={destroy}
                                destroyHidden={destroyHidden}
                              />
                              <ChangeStatusChip processId={processId} choiceId={choiceId} resultId={resultId} />
                              <AchievementButton
                                isAchievementChipVisible={isAchievementChipVisible}
                                processId={processId}
                                choiceId={choiceId}
                                resultId={resultId}
                                newAchievementId={newAchievementId}
                              />

                              <AddingPropertyButton
                                processId={processId}
                                choiceId={choiceId}
                                resultId={resultId}
                                choiceIndex={choiceIndex}
                                isVisible={isChoiceHovered}
                              />
                            </LeftTopContainer>

                            <LeftBottomContainer isShowMultipleLine={isShowMultipleLine} />
                          </LeftContainer>

                          <RightContainer>
                            {!isDragDisabled && (
                              <ResultShowMoreButton
                                isDisabledDeleteItem={isDisabledDeleteItem}
                                processId={processId}
                                choiceId={choiceId}
                                resultId={resultId}
                                nextProcessId={nextProcessId}
                              />
                            )}
                          </RightContainer>
                        </ResultContainer>
                      )}
                    </Draggable>
                  );
                },
              )}

              {provided.placeholder}
            </DroppableContainer>
          )}
        </Droppable>
      </DragDropContext>
    </>
  );
};

Result.propTypes = {
  processId: PropTypes.number.isRequired,
  choiceId: PropTypes.number.isRequired,
  results: PropTypes.arrayOf(PropTypes.object).isRequired,
};

const FlexAlignCenterDiv = styled.div`
  display: flex;
  align-items: center;
`;

const DroppableContainer = styled.div``;

const ResultContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: auto;
  padding: 3px;
  border-top-left-radius: ${props => (props.isResultOnlyOne ? `16px` : `none`)};
  border-top-right-radius: ${props => (props.isResultOnlyOne ? `16px` : `none`)};
  border-bottom-right-radius: ${props => (props.isDefaultResult ? `none` : `16px`)};
  border-bottom-left-radius: ${props => (props.isDefaultResult ? `none` : `16px`)};
  background: ${({ theme, isDefaultResult }) => (isDefaultResult ? `transparent` : ` ${theme.colors.ui_2}`)};

  border-bottom: ${props => (props.isDefaultResult ? '1px solid rgba(103, 74, 246, 0.25)' : ` none`)};
`;

const LeftContainer = styled(FlexAlignCenterDiv)`
  flex-wrap: wrap;
  flex: 1;
`;

const LeftTopContainer = styled(FlexAlignCenterDiv)`
  flex-wrap: wrap;
  width: ${({ isShowMultipleLine }) => isShowMultipleLine && '100%'};

  gap: 4px;
`;

const LeftBottomContainer = styled(FlexAlignCenterDiv)`
  flex-wrap: wrap;
  gap: 4px;

  ${({ isShowMultipleLine }) => {
    if (isShowMultipleLine) {
      return css`
        margin-top: 4px;
      `;
    }

    return css`
      margin-left: 4px;
    `;
  }};
`;

const RightContainer = styled.div`
  display: flex;
  align-items: stretch;
`;

const ResultDivider = styled(ResultDividerSVG)`
  height: 100%;
`;

export default Result;
